import { createContext, useContext, type ReactNode } from 'react';
import { useMatterMemory } from '@/hooks/useMatterMemory';

type MatterMemoryContextValue = ReturnType<typeof useMatterMemory>;

// `null` sentinel so consumers outside the provider throw on access.
const MatterMemoryContext = createContext<MatterMemoryContextValue | null>(null);

/**
 * Shares a single `useMatterMemory` call between the Overview page peek and
 * the `<MatterMemory>` component. ECA reads stay on their own hook.
 */
export function MatterMemoryProvider({
  matterGuid,
  initialPollingEnabled = true,
  children,
}: {
  matterGuid: string | undefined;
  initialPollingEnabled?: boolean;
  children: ReactNode;
}) {
  const value = useMatterMemory(
    matterGuid,
    undefined,
    undefined,
    initialPollingEnabled,
  );
  return (
    <MatterMemoryContext.Provider value={value}>{children}</MatterMemoryContext.Provider>
  );
}

export function useMatterMemoryContext(): MatterMemoryContextValue {
  const value = useContext(MatterMemoryContext);
  if (!value) {
    throw new Error('useMatterMemoryContext must be used within a MatterMemoryProvider');
  }
  return value;
}
